import { memo, useCallback, useLayoutEffect, useRef } from "react"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { cn } from "@/shared/utils/general/cn"
import { ObjectStatus, ObjectType } from "@/db/schema"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import RepeatSvg from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"

// ---------------------------------------------------------------------------
// Register --cb-progress as an animatable <percentage> property.
// Done via JS because Lightning CSS (Tailwind v4) strips @property rules.
// Module-level IIFE — runs exactly once regardless of how many instances.
// ---------------------------------------------------------------------------
;(() => {
  try {
    CSS.registerProperty({
      name: "--cb-progress",
      syntax: "<percentage>",
      inherits: false,
      initialValue: "0%",
    })
  } catch {
    // Already registered or unsupported — both fine.
  }
})()

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CheckboxSize = "small" | "medium" | "large"

interface TaskCheckboxProps {
  status: ObjectStatus
  /** Dotted border for Someday tasks */
  isSomeday?: boolean
  /** Whether this task is pending delayed completion (show pulsing) */
  isPendingCompletion?: boolean
  /** Task type — Project/List render TimelineIcon instead of checkbox */
  taskType?: ObjectType
  /** Custom icon string for Project/List checkboxes */
  icon?: string
  /** Completion percentage (Project pie chart) */
  percentage?: number
  /** Unfinished child count (List bar logic / Project) */
  unfinished?: number
  /** Aggregated checklist progress (completed/total) — renders a ring on the checkbox */
  checklistProgress?: { completed: number; total: number } | null
  /** FIXED recurring template (1) shows ↻ icon instead of checkbox */
  isRecurringTemplate?: 0 | 1
  size?: CheckboxSize
  /** Called on click — caller decides new status */
  onToggle?: () => void
  className?: string
}

// ---------------------------------------------------------------------------
// Size map — matches v1 ChecklistToToDoIcon sizes
// ---------------------------------------------------------------------------

const SIZE_MAP: Record<CheckboxSize, { outer: number; icon: number }> = {
  small: { outer: 18, icon: 14 },
  medium: { outer: 20, icon: 16 },
  large: { outer: 24, icon: 20 },
}

// ---------------------------------------------------------------------------
// SVG paths — from v1 assets/icons/totodo-icons/stroke/
// ---------------------------------------------------------------------------

/** Checkmark-1.svg path: M5 11.4375L10.04 16.5L19 7.5 */
const CHECKMARK_PATH = "M5 11.4375L10.04 16.5L19 7.5"

/** Cross.svg paths: M7 7L12 12M17 17L12 12M12 12L17 7M12 12L7 17 */
const CROSS_PATH = "M7 7L12 12M17 17L12 12M12 12L17 7M12 12L7 17"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SVG checkbox matching v1's ChecklistToToDoIcon.
 *
 * States:
 * - **Unfinished**: Rounded-square border (solid or dotted for Someday)
 * - **Completed**: Primary-filled square with checkmark
 * - **Canceled**: Primary-filled square with cross
 *
 * Pulsing animation plays for 2s on status change to Completed/Canceled.
 */
export const TaskCheckbox = memo(function TaskCheckbox({
  status,
  isSomeday = false,
  isPendingCompletion = false,
  taskType,
  icon,
  percentage = 0,
  unfinished = 0,
  checklistProgress = null,
  isRecurringTemplate = 0,
  size = "medium",
  onToggle,
  className,
}: TaskCheckboxProps) {
  const { outer, icon: iconSize } = SIZE_MAP[size]
  const isFilled =
    status === ObjectStatus.Completed || status === ObjectStatus.Canceled
  const isProjectOrList =
    taskType === ObjectType.Project || taskType === ObjectType.List

  // ── Pulse animation on pending completion ──────────────────────────────
  const isPulsing = isPendingCompletion

  // Every tick that joins the pending batch restarts the pulse of every
  // pending checkbox (v1 behaviour), so the batch pulses in step. Only
  // pulsing checkboxes subscribe to the epoch; the rest never re-render.
  const buttonRef = useRef<HTMLButtonElement>(null)
  const batchEpoch = usePendingCompletions((s) => (isPulsing ? s.batchEpoch : -1))
  useLayoutEffect(() => {
    if (!isPulsing) return
    const pin = (): boolean => {
      const start = usePendingCompletions.getState().batchStartTime
      let found = false
      for (const anim of buttonRef.current?.getAnimations?.() ?? []) {
        if ((anim as CSSAnimation).animationName !== "checkbox-pulse") continue
        found = true
        if (start !== null) anim.startTime = start
        else anim.currentTime = 0
      }
      return found
    }
    // The checkbox that was just ticked only gets its CSS animation a frame or
    // more later (measured: 50ms on Android), so keep trying each frame until
    // it exists — pinning it earlier is a no-op and it ends up out of step.
    let raf = 0
    let frames = 0
    const tryPin = () => {
      if (!pin() && frames++ < 10) raf = requestAnimationFrame(tryPin)
    }
    tryPin()
    return () => cancelAnimationFrame(raf)
  }, [isPulsing, batchEpoch])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onToggle?.()
    },
    [onToggle],
  )

  // Prevent double-click from bubbling to TaskItem's open handler
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  // ── FIXED recurring template: render ↻ icon ────────────────────────────
  // Tasks: gray ↻ icon. Projects/Lists: filled primary circle with white ↻.
  if (isRecurringTemplate === 1) {
    const isContainer =
      taskType === ObjectType.Project || taskType === ObjectType.List
    return (
      <button
        type="button"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={cn(
          "relative z-20 inline-flex shrink-0 items-center justify-center",
          "transition-colors select-none touch-manipulation",
          isContainer
            ? "rounded-full bg-primary text-white"
            : "text-foreground/40",
          className,
        )}
        style={{ width: outer, height: outer }}
        aria-label="Recurring template"
      >
        <RepeatSvg width={iconSize} height={iconSize} />
      </button>
    )
  }

  // ── Project / List: render TimelineIcon ────────────────────────────────
  if (isProjectOrList) {
    const timelineType = taskType === ObjectType.List ? "list" : "project"
    return (
      <button
        type="button"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={cn(
          "relative z-20 inline-flex shrink-0 items-center justify-center",
          "transition-colors select-none",
          className,
        )}
        style={{ width: outer, height: outer }}
        aria-label={
          status === ObjectStatus.Completed
            ? "Completed project"
            : "Open project"
        }
      >
        <DynamicIcon
          icon={icon}
          type={timelineType}
          percentage={percentage}
          unfinished={unfinished}
          status={status}
          size="small"
          color="var(--primary)"
        />
      </button>
    )
  }

  // ── Regular task: checkbox ─────────────────────────────────────────────
  const borderRadius = 5

  const pct = checklistProgress?.total
    ? Math.max(
        0,
        Math.min(
          100,
          (checklistProgress.completed / checklistProgress.total) * 100,
        ),
      )
    : 0

  const inset = 2 // matcha ungefär din stroke/border-känsla (tweak 1–3)

  // Tap target: Apple HIG minimum 44px, but render visual at `outer` (20px)
  // Uses a transparent hit-area expansion via ::after pseudo-element.
  // The button stays at visual size to preserve layout flow.

  return (
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={cn(
          "relative z-20 inline-flex shrink-0 items-center justify-center",
          "transition-colors select-none touch-manipulation",
          // Expand touch target via pseudo-element (no layout shift)
          "before:absolute before:inset-[-12px] before:content-['']",
          isPulsing && "checkbox-pulsing",
          className,
        )}
        style={{ width: outer, height: outer }}
        aria-label={
          status === ObjectStatus.Completed
            ? "Mark as incomplete"
            : status === ObjectStatus.Canceled
              ? "Reactivate task"
              : "Mark as complete"
        }
      >
        {isFilled ? (
          /* Filled state — primary bg with icon */
          <span
            className="flex items-center justify-center rounded bg-primary"
            style={{ width: outer, height: outer, borderRadius }}
          >
            <svg
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              fill="none"
              className={cn(
                "text-primary-foreground",
                isPendingCompletion && status !== ObjectStatus.Completed && status !== ObjectStatus.Canceled && "checkmark-draw",
              )}
            >
              <path
                d={
                  status === ObjectStatus.Completed
                    ? CHECKMARK_PATH
                    : CROSS_PATH
                }
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : (
          /* Unfinished state — CSS border (solid or dashed for Someday) */
          <span
            className="relative block border-foreground/25 transition-colors overflow-hidden"
            style={{
              borderWidth: 1.5,
              width: outer,
              height: outer,
              borderRadius,
              borderStyle: isSomeday ? "dashed" : "solid",
            }}
          >
            {/* Always mounted so CSS transition works between value changes.
                @property --cb-progress is registered as <percentage> so the
                browser interpolates it natively — zero JS timers. */}
            <span
              className="absolute pointer-events-none"
              style={
                {
                  inset,
                  borderRadius: `calc(${borderRadius}px - ${inset}px)`,
                  "--cb-progress": `${pct}%`,
                  transition: "--cb-progress 800ms ease, opacity 400ms ease",
                  background: `conic-gradient(var(--foreground) 0% var(--cb-progress), transparent var(--cb-progress) 100%)`,
                  opacity: pct > 0 ? 0.1 : 0,
                } as React.CSSProperties
              }
            />
          </span>
        )}
      </button>
  )
})
